/**
 * BulkDeleteAction — bulk "Usuń zaznaczone" action for the media-units catalog
 * list.
 *
 * Reuses the bulk-edit-media selection pattern verbatim: operates on the
 * caller's explicit checkbox selection (selectedIds) when present, otherwise
 * the active "select all N matching" filter (selectAllFilter) — mirrors
 * `BulkEditSelectionSchema`'s `{ ids } | { filter }` union and the same
 * 500-row `BULK_EDIT_CAP` as bulk-edit.
 *
 * The "Usuń zaznaczone" trigger lives in the shared floating `BulkActionToolbar`
 * (rendered by the route, as a destructive action); this component owns only the
 * confirm dialog, controlled by the caller's `isOpen`/`onOpenChange` props.
 * Deletion is soft (units move to the 3-day trash) and is enforced per-unit by
 * `media_unit.delete` scope on the server — units the caller may not delete are
 * skipped and reported in the result toast's `failed` count.
 *
 * This component stays permanently mounted in the route tree regardless of
 * `isOpen` (only its rendered output is gated), so the route remounts it via a
 * changing `key` on every open to guarantee a fresh `serverError`.
 *
 * Traceability: bulk-edit-media / Requirement: Bulk "delete selected" action.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { parseApiError } from '@ooh/contracts';
import type { MediaUnitFilters } from '@ooh/contracts';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { Button } from '@ooh/ui/components/button';
import { useBulkDeleteMutation } from '../api/mutations';
import { isBulkSelectionCapExceeded, shouldShowHiddenCount } from '../lib/bulk-selection-cap';

export interface BulkDeleteActionProps {
  /** Holder of media_unit.delete — renders the dialog. */
  canDelete: boolean;
  /** Controlled open state, driven by the shared BulkActionToolbar's "Usuń zaznaczone" action. */
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Explicit checkbox selection (useMediaBulkSelection.selectedIds). */
  selectedIds: number[];
  /** Active "Select all N matching" filter, when the user activated that mode. */
  selectAllFilter: MediaUnitFilters | null;
  /** Total rows matching the current filter (for the confirm count + cap check). */
  totalMatching: number;
  /**
   * Of `selectedIds`, how many are not in the current results (id-based
   * selection only — a filter-based selection has no concept of "hidden").
   */
  hiddenCount?: number;
  /** Called after a successful delete so the caller can clear its selection. */
  onSuccess?: () => void;
}

export function BulkDeleteAction({
  canDelete,
  isOpen,
  onOpenChange,
  selectedIds,
  selectAllFilter,
  totalMatching,
  hiddenCount = 0,
  onSuccess,
}: BulkDeleteActionProps) {
  const { t } = useTranslation('inventory');
  const mutation = useBulkDeleteMutation();
  const [serverError, setServerError] = React.useState<string | null>(null);

  if (!canDelete) return null;

  const isFilterSelection = selectAllFilter != null;
  const effectiveCount = isFilterSelection ? totalMatching : selectedIds.length;
  const capExceeded = isBulkSelectionCapExceeded(
    isFilterSelection,
    totalMatching,
    selectedIds.length,
  );

  async function handleSubmit() {
    if (capExceeded) return;
    setServerError(null);
    try {
      const response = await mutation.mutateAsync({
        selection:
          isFilterSelection && selectAllFilter != null
            ? { filter: selectAllFilter }
            : { ids: selectedIds },
      });
      toast.success(
        t('bulkDelete.resultToast', {
          deleted: response.deleted,
          failed: response.failed,
        }),
      );
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      setServerError(parseApiError(error).message);
    }
  }

  if (!isOpen) return null;

  return (
    <ModalDialog
      header={<span className="text-base font-semibold">{t('bulkDelete.dialogTitle')}</span>}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            data-testid="bulk-delete-cancel-btn"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('bulkDelete.cancel')}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            data-testid="bulk-delete-submit-btn"
            onClick={() => void handleSubmit()}
            disabled={capExceeded || mutation.isPending}
          >
            {mutation.isPending ? '...' : t('bulkDelete.submit')}
          </Button>
        </div>
      }
      onClose={() => onOpenChange(false)}
      testId="bulk-delete-dialog"
    >
      <div className="space-y-4">
        <div
          className="rounded-lg border border-border bg-muted/20 px-4 py-3 text-sm"
          data-testid="bulk-delete-count"
        >
          {t('bulkDelete.confirmMessage', { count: effectiveCount })}
          {shouldShowHiddenCount(isFilterSelection, hiddenCount) ? (
            <div data-testid="bulk-delete-hidden-count">
              {t('bulkDelete.hiddenCount', { count: hiddenCount })}
            </div>
          ) : null}
        </div>
        {capExceeded ? (
          <div
            className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
            data-testid="bulk-delete-cap-warning"
          >
            {t('bulkDelete.capWarning')}
          </div>
        ) : null}
        {serverError ? (
          <div
            className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
            data-testid="bulk-delete-server-error"
          >
            {serverError}
          </div>
        ) : null}
      </div>
    </ModalDialog>
  );
}
